import React, { useState, useEffect } from 'react';
import { apiCall } from '../AuthContext';
import { Plus, Trash2, Edit2, Shield, Loader2, X, FileText } from 'lucide-react';

export default function PrivacyPolicy() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ privacy_policy: '', policy_type: 'privacy_policy' });
  const [saving, setSaving] = useState(false);
  const [viewPolicy, setViewPolicy] = useState(null);

  const fetchPolicies = async () => {
    try {
      const res = await apiCall('/api/privacy-policies', { method: 'GET' });
      setPolicies(res.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchPolicies(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await apiCall(`/api/privacy-policies/${editing}`, { method: 'PUT', data: form });
      } else {
        await apiCall('/api/privacy-policies', { method: 'POST', data: form });
      }
      setShowForm(false);
      setEditing(null);
      setForm({ privacy_policy: '', policy_type: 'privacy_policy' });
      fetchPolicies();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving policy');
    } finally { setSaving(false); }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Delete this policy?')) return;
    try {
      await apiCall(`/api/privacy-policies/${policyId}`, { method: 'DELETE' });
      fetchPolicies();
    } catch { alert('Failed to delete'); }
  };

  const openEdit = (policy) => {
    setEditing(policy.id);
    setForm({ privacy_policy: policy.privacy_policy, policy_type: policy.policy_type || 'privacy_policy' });
    setShowForm(true);
  };

  const policyTypeLabels = {
    'privacy_policy': 'Privacy Policy',
    'end_user_agreement': 'End User Agreement',
    'terms_of_service': 'Terms of Service',
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="privacy-policy-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Policy Management</h2>
          <p className="text-sm text-slate-500 mt-1">Manage privacy policies, terms, and user agreements</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ privacy_policy: '', policy_type: 'privacy_policy' }); }}
          className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-4 py-2.5 text-sm transition-colors"
          data-testid="create-policy-button">
          <Plus className="w-4 h-4" /> New Policy
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="policy-form-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#0F172A]">{editing ? 'Edit Policy' : 'Create Policy'}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Policy Type</label>
                <select value={form.policy_type} onChange={e => setForm(p => ({ ...p, policy_type: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  data-testid="policy-type-select">
                  <option value="privacy_policy">Privacy Policy</option>
                  <option value="end_user_agreement">End User Agreement</option>
                  <option value="terms_of_service">Terms of Service</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Content (HTML supported)</label>
                <textarea value={form.privacy_policy} onChange={e => setForm(p => ({ ...p, privacy_policy: e.target.value }))} required rows={12}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm resize-y font-mono"
                  placeholder="Enter policy content... HTML tags are supported."
                  data-testid="policy-content-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="flex-1 py-2.5 rounded-lg font-medium text-sm border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="policy-submit-button"
                  className="flex-1 bg-[#0052FF] hover:bg-[#0040CC] text-white py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Policy Modal */}
      {viewPolicy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="policy-view-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0F172A]">{policyTypeLabels[viewPolicy.policy_type] || viewPolicy.policy_type}</h3>
              <button onClick={() => setViewPolicy(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="prose prose-sm max-w-none text-slate-600" dangerouslySetInnerHTML={{ __html: viewPolicy.privacy_policy }} />
          </div>
        </div>
      )}

      {/* Policy List */}
      {policies.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="empty-policies">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No policies yet</p>
          <p className="text-sm text-slate-400 mt-1">Create your privacy policy and terms of service</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="policy-list">
          {policies.map(policy => (
            <div key={policy.id}
              className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
              data-testid={`policy-card-${policy.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#0052FF]/10 flex items-center justify-center">
                  {policy.policy_type === 'end_user_agreement' ? <FileText className="w-5 h-5 text-[#0052FF]" /> : <Shield className="w-5 h-5 text-[#0052FF]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-[#0F172A]">{policyTypeLabels[policy.policy_type] || policy.policy_type}</h4>
                  <p className="text-xs text-slate-400">{policy.created_at ? new Date(policy.created_at).toLocaleDateString() : ''}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 line-clamp-3 mb-4">{policy.privacy_policy.replace(/<[^>]*>/g, '').substring(0, 150)}...</p>
              <div className="flex items-center gap-3">
                <button onClick={() => setViewPolicy(policy)} className="text-xs text-[#0052FF] font-medium hover:underline" data-testid={`view-policy-${policy.id}`}>View</button>
                <button onClick={() => openEdit(policy)} className="text-xs text-slate-500 hover:text-[#0052FF] font-medium" data-testid={`edit-policy-${policy.id}`}>Edit</button>
                <button onClick={() => handleDelete(policy.id)} className="text-xs text-slate-500 hover:text-red-500 font-medium" data-testid={`delete-policy-${policy.id}`}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
